import { useEffect, useState } from 'react'

import { DailyVideoTiles } from '@/components/interview/daily-video-tiles'
import { PublicPageHead } from '@/components/public/public-page-head'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { API_ORIGIN, CURRENT_CLIENT } from '@/constants'
import { useDailyCall } from '@/hooks/use-daily-call'
import { usePublicShareRouteParam } from '@/lib/public-share-route-param'

type JoinInfo = {
  session_id: string
  title: string
  room_url: string
  status: string
}

export function InterviewJoinPage({ tokenOverride }: { tokenOverride?: string } = {}) {
  const token = usePublicShareRouteParam('/interview/:token', 'token', tokenOverride)
  const [info, setInfo] = useState<JoinInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [displayName, setDisplayName] = useState('')
  const [creds, setCreds] = useState<{ room_url: string; token: string } | null>(null)
  const [joining, setJoining] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!token) {
        setLoading(false)
        setError('This interview link is invalid.')
        return
      }
      setLoading(true)
      setError(null)
      try {
        const res = await fetch(
          `${API_ORIGIN}/api/public/discourse/join/${encodeURIComponent(token)}`,
        )
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>
        if (!res.ok) {
          throw new Error(
            typeof data.detail === 'string'
              ? data.detail
              : 'This interview link is unavailable or has expired.',
          )
        }
        if (!cancelled) {
          setInfo({
            session_id: String(data.session_id || ''),
            title: String(data.title || 'Interview'),
            room_url: String(data.room_url || ''),
            status: String(data.status || ''),
          })
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Interview not found')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [token])

  const daily = useDailyCall({
    roomUrl: creds?.room_url,
    token: creds?.token,
    enabled: Boolean(creds),
  })

  const onJoin = async () => {
    if (!token) return
    setJoining(true)
    setError(null)
    try {
      const res = await fetch(
        `${API_ORIGIN}/api/public/discourse/join/${encodeURIComponent(token)}/token`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ display_name: displayName.trim() }),
        },
      )
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>
      if (!res.ok) {
        throw new Error(
          typeof data.detail === 'string' ? data.detail : 'Could not join the interview.',
        )
      }
      setCreds({
        room_url: String(data.room_url || ''),
        token: String(data.token || ''),
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not join')
    } finally {
      setJoining(false)
    }
  }

  const brand = CURRENT_CLIENT.brand || CURRENT_CLIENT.name || 'Interview'

  return (
    <div className="mx-auto flex min-h-full max-w-4xl flex-col gap-6 px-4 py-8">
      <PublicPageHead title={info?.title ? `${info.title} · ${brand}` : brand} />
      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{brand}</p>
        <h1 className="text-2xl font-semibold">{info?.title || 'Interview'}</h1>
        <p className="text-sm text-muted-foreground">
          You are joining as the candidate. The interviewer sees live coaching; you will not.
        </p>
      </header>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading interview…</p>
      ) : error && !creds ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : !creds ? (
        <form
          className="max-w-sm space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            void onJoin()
          }}
        >
          <label className="block space-y-1.5 text-sm">
            <span>Your name</span>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
              required
              placeholder="Alex Johnson"
            />
          </label>
          <Button type="submit" disabled={joining || !displayName.trim()}>
            {joining ? 'Joining…' : 'Join interview'}
          </Button>
        </form>
      ) : (
        <div className="space-y-3">
          {daily.error ? (
            <p className="text-sm text-destructive" role="alert">
              {daily.error}
            </p>
          ) : null}
          <DailyVideoTiles
            participants={daily.participants}
            localLabel={displayName.trim() || 'You'}
            remoteLabel="Interviewer"
          />
          <p className="text-sm text-muted-foreground">
            {daily.state === 'joining'
              ? 'Connecting… allow camera and microphone if prompted.'
              : daily.state === 'joined'
                ? 'You are in the interview.'
                : daily.state === 'left'
                  ? 'The interview has ended.'
                  : null}
          </p>
        </div>
      )}
    </div>
  )
}
